<template>
  <div class="line-chart-container pt-5 pr-5">
    <LineChartGenerator
      :chart-options="chartOptions"
      :chart-data="chartData"
      :chart-id="chartId"
      :dataset-id-key="datasetIdKey"
      :plugins="plugins"
      :css-classes="cssClasses"
      :width="width"
      :height="height"
    />
  </div>
</template>

<script>
import { Line as LineChartGenerator } from "vue-chartjs/legacy";
import ChartDataLabels from "chartjs-plugin-datalabels";

import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  LinearScale,
  CategoryScale,
  PointElement,
} from "chart.js";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  LineElement,
  LinearScale,
  CategoryScale,
  PointElement,
  ChartDataLabels
)

export default {
  name: "LineChart",
  components: {
    LineChartGenerator,
  },
  props: {
    chartId: {
      type: String,
      default: "line-chart",
    },
    datasetIdKey: {
      type: String,
      default: "label",
    },
    width: {
      type: Number,
      default: 400,
    },
    height: {
      type: Number,
      default: 400,
    },
    cssClasses: {
      default: "",
      type: String,
    },
    styles: {
      type: Object,
      default: () => {},
    },
    plugins: {
      type: Array,
      default: () => [],
    },
    labels: {
      type: Array,
      default: () => [],
    },
    datasets: {
      type: Array,
      default: () => [],
    },
    interviewTotalCompletion: {
      type: Number,
      default: 0,
    },
    interviewTotalSendToHiringManager: {
      type: Number,
      default: 0,
    },
    activeTab: {
      type: Number,
      default: 0,
    }
  },
  methods: {
    forceChartUpdate() {
      this.$forceUpdate();
    },
  },
  computed: {
    formattedLabels() {
      // Only show "Week X" if activeTab === 1
      if (this.activeTab === 1) {
        return this.labels.map((label, index) => {
          if (index === 0) {
            return label; // First label only (index 0, no "Week X")
          }
          return [label, `Week ${index}`]; // index 1 = "Week 1", index 2 = "Week 2", etc.
        });
      }
      // If activeTab !== 1, return labels as-is
      return this.labels;
    },
    
    chartData() {
      return {
        labels: this.formattedLabels,
        datasets: this.datasets,
      };
    },
    
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          datalabels: {
            display: false,
          },
          legend: {
            position: "bottom",
            labels: {
              font: {
                size: 14,
              },
              usePointStyle: true,
              pointStyle: "rect",
              generateLabels: (chart) => {
                const datasets = chart.data.datasets;
                return datasets.map((dataset, i) => {
                  // Determine which count to show based on dataset label
                  let count = 0;
                  if (dataset.label === 'Interviewed') {
                    count = this.interviewTotalCompletion;
                  } else if (dataset.label === 'Send to Hiring Manager') {
                    count = this.interviewTotalSendToHiringManager;
                  }

                  return {
                    text: `${dataset.label} (${count})`,
                    fillStyle: dataset.borderColor,
                    strokeStyle: dataset.borderColor,
                    lineWidth: 2,
                    hidden: false,
                    index: i,
                    pointStyle: 'rect'
                  };
                });
              }
            },
          },
        },
        scales: {
          x: {
            grid: {
              display: false,
            },
            ticks: {
              autoSkip: false,
              maxRotation: 0,
              minRotation: 0,
              font: {
                size: 11,
              }
            },
            beginAtZero: true,
          },
          y: {
            grid: {
              display: false,
            },
            beginAtZero: true,
          },
        },
      };
    }
  },
  watch: {
    labels(newLabels) {
      this.forceChartUpdate();
    },
    datasets(newDatasets) {
      this.forceChartUpdate();
    },
    interviewTotalCompletion() {
      this.forceChartUpdate();
    },
    interviewTotalSendToHiringManager() {
      this.forceChartUpdate();
    },
    activeTab() {
      this.forceChartUpdate();
    }
  }
};
</script>

<style scoped>
.line-chart-container {
  width: 100%;
  height: 100%;
  max-width: 100%;
  margin: auto;
  position: relative;
}

.line-chart-container canvas {
  height: 280px !important;
}
</style>